/**
 * Sprint 24.5 — InstallStepSuccess (wizard step 4).
 *
 * The success moment + summary recap. Dual CTA (Q11): "Equip on hires →"
 * (primary, opens the hire picker) / "Done — equip later" (tertiary, back to
 * /connections). If discovery returned 0 tools we flip to a verify-state variant
 * (per check 3) rather than claiming success.
 */

import type { CSSProperties } from "react";
import type { FirstPartyTemplate } from "../../../../hooks/use-first-party-templates";

export interface InstallStepSuccessProps {
  template: FirstPartyTemplate;
  toolCount: number;
  toolsetsEnabled?: number;
  toolsetsTotal?: number;
  authorizedAsEmail?: string | null;
  onEquip: () => void;
  onDone: () => void;
}

export function InstallStepSuccess({
  template,
  toolCount,
  toolsetsEnabled,
  toolsetsTotal,
  authorizedAsEmail,
  onEquip,
  onDone,
}: InstallStepSuccessProps) {
  const noTools = toolCount <= 0;

  return (
    <div style={body}>
      <div style={center}>
        <span style={noTools ? warnRing : ring}>
          <i className={`ti ${noTools ? "ti-alert-triangle" : "ti-check"}`} aria-hidden="true" />
        </span>
        {noTools ? (
          <>
            <h3 style={h3}>{template.display_name} connected</h3>
            <p style={sub}>No tools discovered yet — verify the connection on its detail page.</p>
          </>
        ) : (
          <>
            <h3 style={h3}>{template.display_name} connected</h3>
            <p style={sub}>
              {toolCount} tool{toolCount === 1 ? "" : "s"} discovered. Ready to equip on your hires.
            </p>
          </>
        )}
      </div>

      <div style={summary}>
        <SummaryRow label="Tools" value={`${toolCount}`} />
        {toolsetsTotal != null && (
          <SummaryRow label="Toolsets" value={`${toolsetsEnabled ?? 0} of ${toolsetsTotal}`} />
        )}
        {authorizedAsEmail && <SummaryRow label="Authorized as" value={authorizedAsEmail} />}
      </div>

      <div style={footer}>
        <button type="button" style={tertiaryBtn} onClick={onDone}>
          Done — equip later
        </button>
        <button type="button" style={primaryBtn} onClick={onEquip}>
          Equip on hires →
        </button>
      </div>
    </div>
  );
}

function SummaryRow({ label, value }: { label: string; value: string }) {
  return (
    <div style={summaryRow}>
      <span style={summaryLabel}>{label}</span>
      <span style={summaryValue} title={value}>
        {value}
      </span>
    </div>
  );
}

const body: CSSProperties = {
  display: "flex",
  flexDirection: "column",
  gap: 14,
  padding: "24px 28px",
  fontFamily: "var(--font-sans)",
};

const center: CSSProperties = {
  display: "flex",
  flexDirection: "column",
  alignItems: "center",
  textAlign: "center",
  gap: 8,
  padding: "4px 0",
};

const ring: CSSProperties = {
  width: 56,
  height: 56,
  borderRadius: "50%",
  background: "#EAF3DE",
  color: "#173404",
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  fontSize: 26,
  flexShrink: 0,
};

const warnRing: CSSProperties = {
  ...ring,
  background: "#FAEEDA",
  color: "#412402",
};

const h3: CSSProperties = { fontSize: 16, fontWeight: 500, margin: 0, color: "var(--color-text-primary)" };
const sub: CSSProperties = {
  fontSize: 12,
  color: "var(--color-text-secondary)",
  margin: 0,
  maxWidth: 320,
  lineHeight: 1.5,
};

const summary: CSSProperties = {
  background: "var(--color-background-secondary)",
  borderRadius: "var(--border-radius-md)",
  border: "0.5px solid var(--color-border-secondary)",
  padding: "10px 14px",
  display: "flex",
  flexDirection: "column",
  gap: 6,
};

const summaryRow: CSSProperties = { display: "flex", justifyContent: "space-between", gap: 12, minWidth: 0 };
const summaryLabel: CSSProperties = { fontSize: 12, color: "var(--color-text-tertiary)", flexShrink: 0 };
const summaryValue: CSSProperties = {
  fontSize: 12,
  color: "var(--color-text-primary)",
  textAlign: "right",
  overflow: "hidden",
  textOverflow: "ellipsis",
  whiteSpace: "nowrap",
  minWidth: 0,
};

const footer: CSSProperties = {
  display: "flex",
  alignItems: "center",
  justifyContent: "space-between",
  gap: 8,
  marginTop: 4,
};

const tertiaryBtn: CSSProperties = {
  background: "transparent",
  border: "none",
  padding: 0,
  cursor: "pointer",
  color: "var(--color-text-tertiary)",
  fontSize: 12,
  fontFamily: "var(--font-sans)",
};

const primaryBtn: CSSProperties = {
  padding: "8px 16px",
  fontSize: 13,
  fontWeight: 500,
  borderRadius: "var(--border-radius-sm)",
  border: "0.5px solid var(--color-text-primary)",
  background: "var(--color-text-primary)",
  color: "var(--color-background-primary)",
  cursor: "pointer",
  fontFamily: "var(--font-sans)",
};
